/**
 * useProgramFaqItems - Single source of truth for the MasterClass Executive
 * FAQ content. Reads the questions/answers from i18n (`enrollment.faq.*`) and
 * resolves the two variable-driven answers:
 * - price: interpolated from the API-backed `productPriceText` static data,
 *   falling back to the `answerNoPrice` copy when unavailable.
 * - deadline: the fully-formatted end date of the `general_enrollment` window
 *   (`endsAtDisplay`, e.g. "September 24, 2026 at 11:59 PM Pacific Time.") from
 *   the cohort-details API — the final window in which candidates can apply.
 *   Its trailing period is stripped because the copy supplies it. Falls back to the cohort's `applicationDeadline`, and to the
 *   `answerNoDeadline` copy when neither is set.
 *
 * Every FAQ surface (enrollment, program details, subject landing, status
 * portal) consumes this hook so the content stays centralized; each surface
 * keeps its own presentation/tracking wiring.
 */
import type { ReactNode } from 'react'
import { LEGAL_URLS } from '@educator/constants/legalUrls'
import { Trans, useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { useCohortDetails } from '../../api-hooks/enrollment/useCohortDetails'
import type { FaqItem } from './ProgramFaqAccordion'

/** Number of FAQ items defined under `enrollment.faq.items` in the catalog. */
const FAQ_COUNT = 11

interface PricingStaticData {
  productPriceText?: string
  isProductPriceAvailable?: boolean
}

export interface ProgramFaq {
  headline: string
  items: FaqItem[]
}

export const useProgramFaqItems = (): ProgramFaq => {
  const { t, i18n } = useTranslation('@mc/educator')
  const { productPriceText, isProductPriceAvailable } =
    useStaticData<PricingStaticData>()
  const { data: cohortDetails } = useCohortDetails()

  const generalEnrollmentDeadline = cohortDetails?.enrollmentWindows
    ?.find((window) => window.windowType === 'general_enrollment')
    ?.endsAtDisplay?.replace(/\.$/, '')

  const deadline =
    generalEnrollmentDeadline || cohortDetails?.applicationDeadline || ''

  const hasResource = (key: string) =>
    typeof i18n.getResource(i18n.language, '@mc/educator', key) === 'string'

  // Renders an answer key, embedding an external <refundsLink> anchor when the
  // copy contains that marker (Trans), otherwise returning the plain string.
  const renderAnswer = (
    key: string,
    options: Record<string, string>,
  ): ReactNode => {
    const raw = i18n.getResource(i18n.language, '@mc/educator', key)

    if (typeof raw === 'string' && raw.includes('<refundsLink>')) {
      return (
        <Trans
          i18nKey={key}
          values={options}
          components={{
            refundsLink: (
              <a
                href={LEGAL_URLS.refundsInfo}
                target='_blank'
                rel='noopener noreferrer'
                className='underline'
                aria-label={t('enrollment.faq.refundLinkLabel')}
              />
            ),
          }}
        />
      )
    }

    return t(key, options)
  }

  const getAnswerText = (index: number): ReactNode => {
    const answerKeyBase = `enrollment.faq.items.${index}`
    const answerKey = `${answerKeyBase}.answer`
    const answerWithPriceKey = `${answerKeyBase}.answerWithPrice`
    const answerNoPriceKey = `${answerKeyBase}.answerNoPrice`
    const answerNoDeadlineKey = `${answerKeyBase}.answerNoDeadline`
    const hasWithPriceAnswer = hasResource(answerWithPriceKey)
    const hasNoPriceAnswer = hasResource(answerNoPriceKey)

    if (!deadline && hasResource(answerNoDeadlineKey)) {
      return renderAnswer(answerNoDeadlineKey, {})
    }

    if (hasWithPriceAnswer || hasNoPriceAnswer) {
      if (isProductPriceAvailable && productPriceText && hasWithPriceAnswer) {
        return renderAnswer(answerWithPriceKey, {
          price: productPriceText,
          deadline,
        })
      }

      if (hasNoPriceAnswer) {
        return renderAnswer(answerNoPriceKey, { deadline })
      }
    }

    return renderAnswer(answerKey, { deadline })
  }

  const items: FaqItem[] = Array.from({ length: FAQ_COUNT }, (_, index) => ({
    question: t(`enrollment.faq.items.${index}.question`),
    answer: getAnswerText(index),
  }))

  return {
    headline: t('enrollment.faq.headline'),
    items,
  }
}
